import type { PayoutInputs } from '#shared/rules/payoutRules'
import type { ComputedRef } from 'vue'
import { toPayoutInputs } from '#shared/rules/payoutRules'
import { bigIntToNumber } from '~/utils/formatting'
import { useMemberStats } from './useMemberStats'
import { useVaultMetrics } from './useVaultMetrics'

export interface PayoutInputsOptions {
  lazy?: boolean
}

/**
 * The live inputs every payout figure is projected from (`projectPayout` in
 * `shared/rules/payoutRules.ts`), or `null` while any of them is missing.
 *
 * Reads the same keyed fetches as the pool metrics, so it adds no requests.
 */
export function usePayoutInputs(options: PayoutInputsOptions = {}): ComputedRef<PayoutInputs | null> {
  const { lazy = false } = options
  const vaultMetrics = useVaultMetrics({ lazy })
  const memberStats = useMemberStats({ lazy })

  return computed(() => {
    const vault = vaultMetrics.data.value
    const counts = memberStats.data.value?.membersByTier

    return toPayoutInputs({
      aaveApyPercent: vault?.aaveApy ?? null,
      feeRate: vault?.feeRate ?? null,
      totalAssets: vault?.totalAssetsRaw != null && vault.totalAssetsRaw !== ''
        ? bigIntToNumber(BigInt(vault.totalAssetsRaw), 6)
        : null,
      membersByTier: counts ?? null,
    })
  })
}
